import React, { useCallback, useMemo } from 'react';
import { isNull, uniq } from 'lodash/fp';

import styles from './AccessGroups.module.scss';
import { getAccessGroupColumnSort, AccessGroup, getAccessGroupFilters, AccessGroupTableName, getAccessGroupDocuments, getIsFetchingAccessGroupDocumentNames } from './store';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { useAppSelector } from '../../../hooks/react-redux';
import { Toggle } from '../../shared/toggle';
import { FilterDropdownOptions } from '../../shared/table/types';
import { DropdownOption } from '../../shared/dropdown/Dropdown';
import { AccessGroupTableHeader } from './AccessGroupTableHeader';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';

interface AccessGroupDocumentsTableProps {
    documentNameIds: number[];
    updateGroup: (key: keyof AccessGroup, ids: number[]) => void;
    scrollableHeight: number;
}

export const AccessGroupDocumentsTable: React.FC<AccessGroupDocumentsTableProps> = ({ documentNameIds, updateGroup, scrollableHeight }) => {

    const columnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT));
    const filters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.DOCUMENT));
    const documentNames = useAppSelector(getAccessGroupDocuments);
    const isFetchingDocumentNames = useAppSelector(getIsFetchingAccessGroupDocumentNames);

    const toggleDocumentInAccessGroup = useCallback((documentNameId: number, value: boolean) => {
        const updatedDocumentNameIds = !value ? documentNameIds.filter(id => id !== documentNameId) : [...documentNameIds, documentNameId];
        updateGroup('documentNameIds', updatedDocumentNameIds);
    }, [documentNameIds, updateGroup]);

    const documentInGroup = useCallback((documentNameId: number) => documentNameIds.includes(documentNameId), [documentNameIds]);

    const allSelected = useMemo(() => documentNames.every(({ documentNameId }) => documentNameIds.includes(documentNameId!)), [documentNameIds, documentNames]);

    const toggleSelectAll = useCallback(() => {
        const currentDocumentNameIds = documentNames.map(({ documentNameId }) => documentNameId!);
        let updatedDocumentNameIds = [];
        if (allSelected) {
            updatedDocumentNameIds = documentNameIds.filter(id => !currentDocumentNameIds.includes(id));
        } else {
            updatedDocumentNameIds = uniq([...documentNameIds, ...currentDocumentNameIds]);
        }
        updateGroup('documentNameIds', updatedDocumentNameIds);
    }, [allSelected, documentNames, updateGroup, documentNameIds]);

    const agreementDropdownOptions: DropdownOption[] = uniq(documentNames.filter(({ agreementType }) => !isNull(agreementType)).map(({ agreementType }) => agreementType!)).map(value => ({ label: value, value }));
    const filterDropdownOptions: FilterDropdownOptions = { agreementType: agreementDropdownOptions };

    const tableHeaders = [
        { width: '50%', title: 'Name', column: 'documentName' },
        { width: '30%', title: 'Agreement Type', column: 'agreementType' },
        { width: '20%', title: 'Included', column: 'documentIncluded' }
    ];

    return (
        <div className={styles.accessGroupContentWrapper}>
            <AccessGroupTableHeader
                allSelected={allSelected}
                toggleSelectAll={toggleSelectAll}
                textFilterColumns={['documentName']}
                tableHeaders={tableHeaders}
                selectAllLabel='Document Types'
                filterDropdownOptions={filterDropdownOptions}
                filters={filters}
                columnSort={columnSort}
                tableName={AccessGroupTableName.DOCUMENT}
            />
            <Scrollable height={`${scrollableHeight}px`}>
                {isFetchingDocumentNames ? <div className={styles.tableLoadingDots} style={{ height: scrollableHeight * 2 }}><LoadingDots /></div> :
                    documentNames.map(({ documentNameId, documentName, agreementType }) => {
                        const inGroup = documentInGroup(documentNameId!);
                        return (
                            <div key={documentNameId} className={styles.tableRow}>
                                <div className={styles.tableCell} style={{ width: '50%' }}>{documentName}</div>
                                <div className={styles.tableCell} style={{ width: '30%' }}>{agreementType}</div>
                                <div className={styles.tableCell} style={{ width: '20%' }}>
                                    <Toggle
                                        onChange={value => toggleDocumentInAccessGroup(documentNameId!, value)}
                                        checked={inGroup}
                                    />
                                </div>
                            </div>
                        );
                    })
                }
            </Scrollable>
        </div>
    );
};
